import { z } from 'zod'
import isIban from 'validator/lib/isIBAN'

export const NewsletterContactSchema = z.object({
    email: z.string().email(),
})

export const FindPolicyFormSchema = z.object({
    policy_number: z.string().trim().min(1),
    claim_pin: z.string().trim().min(1),
})

export const LoginLinkFormSchema = z.object({
    email: z.string().email().trim().min(1),
})

export const ClaimTypeFormSchema = z.object({
    claim_type: z.string().trim().min(1),
})

export const WhenFormSchema = z.object({
    claim_date: z.coerce
        .date()
        .max(new Date(new Date().toDateString()))
        .transform((val) => val.toDateString()),
})

export const DamageDiagnosisFormSchema = z.record(z.string(), z.enum(['true', 'false']))

export const DetailsFormSechma = z.object({
    additional_info: z.string().trim().min(80),
})

export const PaymentDetailsFormSchema = z.object({
    transfer_name: z.string().trim().min(1),
    transfer_iban: z.string().trim().min(1).refine(isIban),
})

export const CardDetailsFormSchema = z.object({
    card_number: z
        .string()
        .trim()
        .min(13)
        .max(19)
        .regex(/^\d{13,19}$/),
    card_expiry: z
        .string()
        .trim()
        .regex(/^(0[1-9]|1[0-2])\/\d{2}$/)
        .refine((value) => {
            const [monthStr, yearStr] = value.split('/')
            const month = parseInt(monthStr, 10)
            const year = parseInt(yearStr, 10)

            const now = new Date()
            const currentYear = now.getFullYear() % 100
            const currentMonth = now.getMonth() + 1 

            if (year > currentYear) return true
            if (year === currentYear && month >= currentMonth) return true
            return false
        }),
    card_cvc: z
        .string()
        .trim()
        .regex(/^\d{3,4}$/),
})

export const ContactInformationFormSchema = z
    .object({
        name: z.string().trim().min(1),
        email: z.string().email(),
        phone: z.string().regex(new RegExp('^\\+[1-9]{1}[0-9]{7,14}$')),
        address: z.string().max(100),
        zip: z.string().max(5),
        city: z.string().max(40),
    })
    .passthrough()

export const CustomerInfoFormSchema = z
    .object({
        email: z.string().email(),
        address: z.string().max(100).trim().min(1),
        zip: z.string().max(5).trim().min(1),
        city: z.string().max(40).trim().min(1),
        country: z.string().min(1),
    })
    .passthrough()

export const PolicyDataFormSchema = z.object({
    policy_date: z.coerce
        .date()
        .transform((val) => val.toDateString()), 
    });

export const ExperienceFormSchema = z.object({
    vote_count: z.string(),
    vote_description: z.string(),
})

export const GoalsFormSchema = z.object({
    goals_option: z.string(),
    goals_description: z.string(),
})

export const InsuranceFormSchema = z.object({
    insurance_options: z.array(z.string()),
    insurance_description: z.string(),
})

export const formSchemaMapping = {
    'find-policy': FindPolicyFormSchema,
    'login-link': LoginLinkFormSchema,
    'claim-type': ClaimTypeFormSchema,
    when: WhenFormSchema,
    'damage-diagnosis': DamageDiagnosisFormSchema,
    details: DetailsFormSechma,
    'contact-information': ContactInformationFormSchema,
    'payment-details': PaymentDetailsFormSchema,
    'card-details': CardDetailsFormSchema,
    'policy-date': PolicyDataFormSchema,
    'customer-info': CustomerInfoFormSchema,
    experience: ExperienceFormSchema,
    goals: GoalsFormSchema,
    insurance: InsuranceFormSchema,
}

export type SupportedForm = keyof typeof formSchemaMapping
